/**
 * Web multipart upload transport.
 *
 * Browsers have real `FormData`/`File`/`Blob`, so the standard fetch multipart
 * upload works. Prefer the retained `File` reference (avoids stale blob-URL
 * issues); fall back to re-fetching a `blob:` URL when only the URI is known
 * (e.g. draft restore).
 */

import type { MultipartResponse, SendMultipartArgs } from './upload-transport.types';

async function resolveWebFile(
  file: SendMultipartArgs['file']
): Promise<File> {
  if (file.file) {
    return file.file;
  }
  const response = await fetch(file.uri);
  const blob = await response.blob();
  return new File([blob], file.name || 'photo.jpg', {
    type: file.type || 'image/jpeg',
  });
}

export async function sendMultipart({
  url,
  file,
  fieldName,
  parameters,
  headers,
  signal,
}: SendMultipartArgs): Promise<MultipartResponse> {
  const webFile = await resolveWebFile(file);

  const formData = new FormData();
  formData.append(fieldName, webFile);
  if (parameters) {
    Object.entries(parameters).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  // Do not set Content-Type: the browser adds the multipart boundary.
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
    signal,
  });

  const body = await response.text();
  return {
    status: response.status,
    ok: response.ok,
    contentType: response.headers.get('content-type'),
    body,
  };
}
